import React, { useMemo, useState } from 'react';

const wrap = { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '6px 8px', border: '1.5px solid #E5E7EB', borderRadius: 7, minHeight: 40, boxSizing: 'border-box' };
const chip = { display: 'inline-flex', alignItems: 'center', gap: 4, background: '#FEF3E2', color: '#B15400', fontSize: 12, fontWeight: 600, borderRadius: 999, padding: '3px 6px 3px 10px', whiteSpace: 'nowrap' };
const chipRemove = { border: 'none', background: 'none', cursor: 'pointer', color: '#B15400', fontSize: 13, lineHeight: 1, padding: 0 };
const textInput = { flex: 1, minWidth: 100, border: 'none', outline: 'none', fontSize: 14, fontFamily: 'inherit', padding: '4px 2px' };
const dropdown = { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 7, marginTop: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10, maxHeight: 180, overflowY: 'auto' };
const dropdownItem = { padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: '#374151' };

/** Chip-style tag input with autocomplete against existing tags — typing shows matching
 *  suggestions to click, or Enter/comma creates a brand-new tag. Backspace on an empty
 *  field removes the last chip. Deliberately doesn't require picking from `suggestions` —
 *  any string can become a tag, so this never turns back into a managed taxonomy. */
export default function TagInput({ value, onChange, suggestions = [], placeholder }) {
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);

  const normalize = (s) => s.trim().toLowerCase();

  const addTag = (raw) => {
    const tag = normalize(raw);
    if (tag && !value.includes(tag)) onChange([...value, tag]);
    setText('');
  };

  const removeTag = (tag) => onChange(value.filter((t) => t !== tag));

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (text.trim()) addTag(text);
    } else if (e.key === 'Backspace' && !text && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  const matches = useMemo(() => {
    const q = normalize(text);
    if (!q) return [];
    return suggestions.filter((s) => s.includes(q) && !value.includes(s)).slice(0, 8);
  }, [text, suggestions, value]);

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ ...wrap, borderColor: focused ? '#F29213' : '#E5E7EB' }}>
        {value.map((tag) => (
          <span key={tag} style={chip}>
            {tag}
            <button type="button" style={chipRemove} onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`}>×</button>
          </span>
        ))}
        <input
          style={textInput}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); if (text.trim()) addTag(text); }}
          placeholder={value.length === 0 ? (placeholder || 'Type a tag and press Enter…') : ''}
        />
      </div>
      {focused && matches.length > 0 && (
        <div style={dropdown}>
          {matches.map((s) => (
            <div key={s} style={dropdownItem} onMouseDown={(e) => { e.preventDefault(); addTag(s); }}>
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
